import React, { memo, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationSystem } from '../../../data/Endpoints-API';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AuthContext } from '../../../contexts/Auth-Context';
import { useChartContext } from '../../../contexts/Chart-Context';
import { useMetricsContext } from '../../../contexts/Metrics-Context';
import { FaClock } from 'react-icons/fa';
import { adjustDateTime, formatDate } from '../../../utils/format';
import { localizeNotificationMessage } from '../../../i18n/notificationMessage.js';
import '../notifications/NotificationDropdown.scss';

function SystemNotification() {
  const { t } = useTranslation();
  const { user, reloadApi } = useContext(AuthContext);  // Get the user context
  const { selectedStyle } = useChartContext();
  const { utcOffset } = useMetricsContext();
  const [systemNotificationUpdates, setUpdates] = useState([]); 
  const [systemNotificationLoading, setLoading] = useState(true); 
  const [currentPage, setCurrentPage] = useState(1); 
  const [hasMore, setHasMore] = useState(true);  
  const bottomRef = useRef(null);
  const emptyToastShownRef = useRef(false);
  const PER_PAGE = 15;

  // Function to fetch updates from the backend with pagination support
  const fetchUpdatesNotification = useCallback(async (page) => {
    // Avoid calling API before auth user is ready
    if (!user?.userId || user.userId <= 0) {
      setLoading(false);
      return;
    }

    setLoading(true); // Set loading to true when we start fetching
    try {
      const response = await axios.post(notificationSystem(), {
        userId: user.userId,
        page,  
        perPage: PER_PAGE,  
      });

      if (response.data && Array.isArray(response.data.notifications)) {
        const notifications = response.data.notifications;

        // Show a one-time info toast when the first page is empty
        if (page === 1 && notifications.length === 0 && !emptyToastShownRef.current) {
          emptyToastShownRef.current = true;
        }

        // Reset toast suppression if data appears later
        if (notifications.length > 0) {
          emptyToastShownRef.current = false;
        }

        setHasMore(notifications.length === PER_PAGE);
        setUpdates(prevUpdates =>
          page === 1 ? [...notifications] : [...prevUpdates, ...notifications]
        );
      } else {
        setHasMore(false);
        setUpdates([]);
      }
    } catch (error) {
      setHasMore(false);
      setUpdates([]);
      // console.error("Error fetching updates:", error);
    } finally {
      setLoading(false); // Set loading to false once fetching is complete
    }
  }, [user?.userId]);

  useEffect(() => {
    // Fetch after userId is available and on reloadApi changes
    if (!user?.userId || user.userId <= 0) return;

    setLoading(true);
    setUpdates([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchUpdatesNotification(1);
  }, [reloadApi, user?.userId, fetchUpdatesNotification]);

  useEffect(() => {
    if (currentPage !== 1) {
      fetchUpdatesNotification(currentPage); 
    }
  }, [currentPage, fetchUpdatesNotification]);

  // Handle the scroll event with requestAnimationFrame for smooth performance
  const handleScroll = useCallback(() => {
    if (!bottomRef.current) return;
    
    requestAnimationFrame(() => {
      const container = bottomRef.current;
      if (container && !systemNotificationLoading && hasMore) {
        const { scrollHeight, scrollTop, clientHeight } = container;
        // Check if the user has scrolled to the bottom
        if (scrollHeight - scrollTop <= clientHeight + 50) {
          setCurrentPage(prevPage => prevPage + 1);
        }
      }
    });
  }, [systemNotificationLoading, hasMore]);

  // Scroll event listener setup with passive option for better performance
  useEffect(() => {
    const container = bottomRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);  // Use memoized handleScroll

  return (
      <div
        className="system-notification"
        ref={bottomRef}
        onScroll={handleScroll}
      >
        {systemNotificationLoading && currentPage === 1 ? (
          <div className="loading">
            <span>{t('notifications.loading')}</span>
          </div>
        ) : systemNotificationUpdates.length > 0 ? (
          <div className="notification-list-item">
            {systemNotificationUpdates.map((notification) => (
              <div key={notification.id} className="notification-item">
                <div className="notification-content-wrapper">
                  <div className="notification-text">
                    <p>{localizeNotificationMessage(notification.notification, t)}</p>
                  </div>
                  <div className="notification-meta">
                    <div className="notification-time" style={{ color: selectedStyle.buyColor }}>
                      <FaClock size={12} />
                      <span>{formatDate(adjustDateTime(notification.created_at, utcOffset))}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {systemNotificationLoading && (
              <div className="loading">
                <span>{t('notifications.loadingMore')}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="no-notifications">
            <div style={{ fontSize: '14px', color: '#8b92b0' }}>{t('notifications.noSystemNotifications')}</div>
          </div>
        )}
      </div>
  );
}

export default memo(SystemNotification);
